import { Button, ButtonGroup, Stack, Text } from '@chakra-ui/react';
import { TimeOfDay } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { timeOfDayOptions } from '@pelicargo/utils';
import { addDays, isSameDay, parseISO } from 'date-fns';
import { useCallback } from 'react';
import { FieldPathByValue, PathValue, useFormContext, useWatch } from 'react-hook-form';

import { SimpleXProps } from '../SimpleField';
import { DayType } from './Calendar/type';
import { DateTimePicker } from './DateTimePicker';

export type Props<FormSchema> = SimpleXProps<FormSchema> & {
  name: FieldPathByValue<FormSchema, string | Date>;
  variant?: string;
  matchWidth?: boolean;
  timeOfDay?: boolean;
  timeOfDayName?: FieldPathByValue<FormSchema, TimeOfDay>;
  iconFontSize?: string; // Remove this?? It's unused
  placeholder?: string;
  fontSize?: string;
  onChange?: (value: string) => void;
};

export const SimpleDatePicker = <FormSchema extends {}>({
  name,
  timeOfDay = false,
  timeOfDayName,
  onChange,
  ...rest
}: Props<FormSchema>) => {
  const { setValue } = useFormContext<FormSchema>();
  const formValue = useWatch({ name });
  const timeOfDayValue = useWatch({ name: timeOfDayName ?? '' });

  const handleSetDate = useCallback(
    (nextDate: Date) => {
      const utcDate = new Date(Date.UTC(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate()));
      setValue(
        name,
        utcDate.toISOString() as PathValue<FormSchema, keyof FormSchema & FieldPathByValue<FormSchema, string | Date>>,
      );
      onChange?.(utcDate.toISOString());
    },
    [setValue, name, onChange],
  );

  const onDayTypeRulesFn = useCallback(
    (date: Date) => {
      const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const todayUtc = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()));

      if (utcDate < addDays(todayUtc, -1)) return DayType.DISABLE;

      const formValueDate = formValue ? parseISO(formValue) : null;
      const hasValueAndIsSame = formValueDate && isSameDay(utcDate, formValueDate);
      if (hasValueAndIsSame) return DayType.ACTIVE;

      return DayType.NORMAL;
    },
    [formValue],
  );

  return (
    <Stack w="full" flexDir="column" {...rest}>
      <Show if={timeOfDay}>
        <Text textStyle="body">Tender date</Text>
      </Show>
      <DateTimePicker
        selectedDate={formValue ? parseISO(formValue) : new Date()}
        onSetDate={handleSetDate}
        onDayTypeRulesFn={onDayTypeRulesFn}
      />
      <Show if={timeOfDay}>
        <Stack spacing="2">
          <Text textStyle="body">Time</Text>
          <ButtonGroup w="full">
            {timeOfDayOptions.map((option) => (
              <Button
                key={option.value}
                value={option.value}
                size="xs"
                w="full"
                bg={option.value === timeOfDayValue ? 'gray.800' : 'gray.100'}
                color={option.value === timeOfDayValue ? 'white' : 'gray.800'}
                _hover={{
                  bg: option.value === timeOfDayValue ? 'gray.800' : 'gray.200',
                }}
                onClick={() =>
                  setValue(
                    timeOfDayName,
                    option.value as PathValue<FormSchema, FieldPathByValue<FormSchema, TimeOfDay>>,
                  )
                }
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>
      </Show>
    </Stack>
  );
};
